import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import LineArrowLeft from "../../../../assets/icons/LineArrowLeft";
import ReplyIcon from "../../../../assets/icons/ReplyIcon";
import StarFilledIcon from "../../../../assets/icons/StarFilledIcon";
import StarIcon from "../../../../assets/icons/StarIcon";
import Loader from "../../../../components/ui/Loader";
import {
  useDeleteMailMutation,
  useGetReplyMailByParentMailQuery,
  useGetSingleMailByIdQuery,
  useStarredMailMutation,
} from "../../../../features/superAdmin/ticketService/ticketServiceApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import CommonModalLayout from "../../../../layouts/CommonModalLayout";
import { formatDate, formatTimeBD } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { SingleMail } from "../../../../types/mail";
import { capitalize } from "../../../../utils/string";
import Email from "../Email";
import EmailAttachments from "./_components/EmailAttachments";
import EmailHeader from "./_components/EmailHeader";
import ReplyEmail from "./_components/ReplyEmail";

type ModalData = { name: string; formData: object };

export default function SingleEmail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAppSelector((state) => state.auth);
  const { open, handleOpen, handleClose } = useModal<ModalData, "CONFIRM">();

  // Component States
  const [replying, setReplying] = useState(false);

  // Queries
  const {
    data: emailData,
    isError: isEmailError,
    error: emailError,
    isLoading: isEmailLoading,
  } = useGetSingleMailByIdQuery(id);
  const email = (emailData?.data || {}) as SingleMail;

  // getRMByParentEmail -> getReplyMailByParentEmail
  const {
    data: getReplyMails,
    isError: isRMError,
    error: RMError,
  } = useGetReplyMailByParentMailQuery(id);

  // Mutations
  const [
    deleteEmail,
    {
      isSuccess: emailDeleted,
      isError: isEmailDeleteError,
      error: emailDeleteError,
      isLoading: emailDeleting,
    },
  ] = useDeleteMailMutation();

  const [toggleEmailStar, { isSuccess: isToggledEmailStar }] =
    useStarredMailMutation();

  // Handler functions
  const handleDeleteMail = () => {
    deleteEmail({
      id: id,
      data: {
        userId: auth.user?.id,
      },
    });
  };

  const handleToggleStar = (id: any) => {
    toggleEmailStar({
      id: id,
      data: {
        userId: auth?.user?.id,
      },
    });
  };

  // Selectors
  const isSender = auth?.user?.id === email?.sender_id;
  const isReceiver = auth?.user?.id === email?.receiver_id;
  const bccUser = email?.bcc_users?.find(
    (user: any) => user?.MailUserBcc?.user_id === auth?.user?.id,
  );
  const isStarredBcc = bccUser?.MailUserBcc?.is_starred;

  const ccUser = email?.cc_users?.find(
    (user: any) => user?.MailUserCc?.user_id === auth?.user?.id,
  );
  const isStarredCc = ccUser?.MailUserCc?.is_starred;

  // Errors
  useError(isEmailError, emailError, "Get email failed");
  useError(isEmailDeleteError, emailDeleteError, "Email delete failed");
  useError(isRMError, RMError, "Get replay email failed");

  // Effects
  useEffect(() => {
    if (emailDeleted) {
      toast("success", "Email deleted successfully!");
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailDeleted]);

  useEffect(() => {
    if (isToggledEmailStar) {
      toast("success", "Success");
    }
  }, [isToggledEmailStar]);

  if (isEmailLoading) {
    return <Loader />;
  }

  const renderStarTogglerContent = (email: SingleMail, time: string) => {
    return (
      <div className="flex gap-4 items-center">
        <div>
          {formatDate(time || "")} - {formatTimeBD(time || "")}
        </div>
        {isSender === isReceiver && (
          <div
            className="cursor-pointer"
            onClick={() => handleToggleStar(email?.id)}
          >
            {email?.is_starred_by_sender === true ? (
              <StarFilledIcon />
            ) : (
              <StarIcon />
            )}
          </div>
        )}

        {isSender && isSender !== isReceiver && (
          <div
            className="cursor-pointer"
            onClick={() => handleToggleStar(email?.id)}
          >
            {email?.is_starred_by_sender === true ? (
              <StarFilledIcon />
            ) : (
              <StarIcon />
            )}
          </div>
        )}

        {isReceiver && isSender !== isReceiver && (
          <div
            className="cursor-pointer"
            onClick={() => handleToggleStar(email?.id)}
          >
            {email?.is_starred_by_receiver === true ? (
              <StarFilledIcon />
            ) : (
              <StarIcon />
            )}
          </div>
        )}

        {bccUser && (
          <div
            className="cursor-pointer"
            onClick={() => handleToggleStar(email?.id)}
          >
            {isStarredBcc === true ? <StarFilledIcon /> : <StarIcon />}
          </div>
        )}

        {ccUser && (
          <div
            className="cursor-pointer"
            onClick={() => handleToggleStar(email?.id)}
          >
            {isStarredCc === true ? <StarFilledIcon /> : <StarIcon />}
          </div>
        )}

        <div onClick={() => setReplying(true)} className="cursor-pointer">
          <ReplyIcon />
        </div>
      </div>
    );
  };

  return (
    <>
      {open?.type === "CONFIRM" && (
        <CommonModalLayout
          title="Are you sure email deleted?"
          deleteButton
          open={open?.open}
          handleClose={handleClose}
          handleAction={handleDeleteMail}
          alertRedIcon
          cancelButton
          loading={emailDeleting}
        />
      )}

      <Email>
        {/* Email Header */}
        <div className="border !border-[#cccccc] rounded-[10px] ">
          <div className="flex gap-6 items-center mt-4 border-b !border-b-[#cccccc] pl-4 pr-4 pb-3">
            <div className="cursor-pointer" onClick={() => navigate(-1)}>
              <LineArrowLeft />
            </div>

            <div
              className="cursor-pointer"
              onClick={() => {
                handleOpen({
                  type: "CONFIRM",
                });
              }}
            >
              <DeleteIcon color="#5E718D" />
            </div>
          </div>

          <div className="flex items-center gap-4 mt-2 pl-4 pr-4">
            <div className="text-[28px] mb-2">{email?.subject}</div>
            <div className="text-xs bg-extra-light rounded px-1 inline-block">
              {capitalize(isSender ? "Sent" : "Inbox")}
            </div>
          </div>

          <div className="border-b border-b-light mb-3 pl-4 pr-4">
            <div className="flex justify-between mt-2">
              <EmailHeader email={email} />

              {renderStarTogglerContent(email, email?.created_at)}
            </div>

            {/* Email Body */}
            <div className="my-4 ml-[75px] text-display-body">{email.body}</div>

            <EmailAttachments attachments={email?.attachments as string[]} />
          </div>

          {getReplyMails?.data?.map((mail: any) => (
            <div className="border-b border-b-light mb-3 pl-4 pr-4">
              <div className="flex justify-between mt-2">
                <EmailHeader email={mail as SingleMail} />

                {renderStarTogglerContent(email, mail?.created_at)}
              </div>

              {/* Email Body */}
              <div className="my-4 ml-[75px] text-display-body">
                {mail?.body}
              </div>

              <EmailAttachments attachments={mail?.attachments as string[]} />
            </div>
          ))}

          {/* Email Reply */}
          <ReplyEmail replies={getReplyMails} setReplying={setReplying} replying={replying} />
        </div>
      </Email>
    </>
  );
}
